import React, { useState, useRef, useEffect } from 'react';
import { VideoSegment, VideoLength } from '../types';
import { QuotaService } from '../services/quotaService';
import { getAvailability, AvailabilityStatus } from '../services/availabilityService';
import { adminDriveService } from '../services/adminDriveService';
import QuotaDisplay from './QuotaDisplay';

interface SeamlessVideoDisplayProps {
  script: string;
  videoLength: VideoLength | number;
  onGenerateSegment: (segmentNumber: number, scriptContent: string) => Promise<string>;
  topic?: string; // For generating meaningful filenames
}

const SeamlessVideoDisplay: React.FC<SeamlessVideoDisplayProps> = ({
  script,
  videoLength,
  onGenerateSegment,
  topic = 'video'
}) => {
  const [segments, setSegments] = useState<VideoSegment[]>([]);
  const [currentGenerating, setCurrentGenerating] = useState<number | null>(null);
  const [playbackReady, setPlaybackReady] = useState(false);
  const [expandedSegments, setExpandedSegments] = useState<Set<number>>(new Set());
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [quotaRefreshTrigger, setQuotaRefreshTrigger] = useState<number>(0);
  const [quotaAvailable, setQuotaAvailable] = useState<boolean>(true);
  const [remainingQuota, setRemainingQuota] = useState<number>(3);
  const [availability, setAvailability] = useState<AvailabilityStatus>({
    videoEnabled: false,
    videoReason: 'INIT',
    remaining: null,
    quotaTotal: null,
    quotaUsed: null
  });
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Check availability on mount
  useEffect(() => {
    const checkAvailability = async () => {
      const avail = await getAvailability();
      setAvailability(avail);
      
      // Backward compatibility with quota
      const hasQuota = avail.videoEnabled && (avail.remaining === null || avail.remaining > 0);
      setQuotaAvailable(hasQuota);
      setRemainingQuota(avail.remaining ?? 3);
    };
    checkAvailability();
  }, [quotaRefreshTrigger]);

  // Parse script into segments
  useEffect(() => {
    const parseSegments = () => {
      const segmentTexts: VideoSegment[] = [];
      const isCustomLength = typeof videoLength === 'number';
      
      if (isCustomLength) {
        // For custom length, treat as single video
        segmentTexts.push({
          segmentNumber: 1,
          duration: videoLength as number,
          scriptContent: script,
        });
      } else {
        // Parse segments for preset lengths
        const segmentRegex = /SEGMENT (\d+)[^]*?(?=SEGMENT \d+|$)/gi;
        
        let match;
        while ((match = segmentRegex.exec(script)) !== null) {
          const segmentContent = match[0].trim();
          const segmentNumber = parseInt(match[1]);
          
          // Extract transition if exists
          const transitionMatch = segmentContent.match(/Transition:\s*([^\n]+)/i);
          
          segmentTexts.push({
            segmentNumber: segmentNumber,
            duration: 8,
            scriptContent: segmentContent,
            transition: transitionMatch?.[1]?.trim(),
          });
        }

        // If no segments found, treat entire script as one segment
        if (segmentTexts.length === 0 && script) {
          segmentTexts.push({
            segmentNumber: 1,
            duration: 8,
            scriptContent: script,
          });
        }
      }

      setSegments(segmentTexts);
    };

    parseSegments();
  }, [script]);

  const handleGenerateSegment = async (segment: VideoSegment) => {
    console.log("=== HANDLE GENERATE SEGMENT CALLED ===");
    console.log("Segment number:", segment.segmentNumber);
    console.log("Script content length:", segment.scriptContent?.length);
    
    if (currentGenerating !== null) {
      console.log("Already generating another segment, skipping...");
      return;
    }
    
    // Check availability before generating
    console.log("Checking availability...");
    const avail = await getAvailability();
    setAvailability(avail);
    
    const canGenerate = avail.videoEnabled && (avail.remaining === null || avail.remaining > 0);
    console.log("Can generate:", canGenerate, "Reason:", avail.videoReason);
    
    if (!canGenerate) {
      console.error("Cannot generate video!");
      let errorMsg = '영상 생성을 할 수 없습니다. ';
      if (!avail.videoEnabled) {
        if (avail.videoReason === 'MISSING_API_KEY') {
          errorMsg += '서버에 API 키가 설정되지 않았습니다.';
        } else {
          errorMsg += '영상 생성 서비스가 비활성화되었습니다.';
        }
      } else if (avail.remaining === 0) {
        errorMsg += '오늘의 생성 한도를 모두 사용했습니다.';
      }
      setGenerationError(errorMsg);
      return;
    }

    setCurrentGenerating(segment.segmentNumber);
    setGenerationError(null);
    
    try {
      console.log(`Starting video generation for segment ${segment.segmentNumber}...`);
      console.log("Calling onGenerateSegment with:", {
        segmentNumber: segment.segmentNumber,
        scriptContentLength: segment.scriptContent?.length
      });
      
      const videoUrl = await onGenerateSegment(segment.segmentNumber, segment.scriptContent);
      
      console.log(`Segment ${segment.segmentNumber} generation completed!`);
      console.log("Returned video URL:", videoUrl);
      console.log("URL type:", typeof videoUrl);
      console.log("URL starts with 'veo:'?", videoUrl?.startsWith('veo:'));
      console.log("URL starts with 'data:video/'?", videoUrl?.startsWith('data:video/'));
      console.log("URL starts with 'storyboard:'?", videoUrl?.startsWith('storyboard:'));
      
      // Auto-upload to admin Drive (fire and forget)
      if (videoUrl && !videoUrl.startsWith('storyboard:') && !videoUrl.startsWith('data:')) {
        const fileName = adminDriveService.generateFileName(topic, segment.segmentNumber, 8);
        adminDriveService.uploadVideoToAdminDrive(videoUrl, fileName, {
          topic,
          duration: 8,
          segment: segment.segmentNumber
        }).catch(err => {
          console.warn('Admin Drive upload failed (non-blocking):', err);
        });
      }
      
      setSegments(prev => prev.map(s => 
        s.segmentNumber === segment.segmentNumber
          ? { ...s, videoUrl }
          : s
      ));

      // Trigger quota refresh after successful segment generation
      setQuotaRefreshTrigger(prev => prev + 1);

      // Check if all segments are ready
      const allReady = segments.every(s => 
        s.segmentNumber === segment.segmentNumber ? true : !!s.videoUrl
      );
      if (allReady) {
        setPlaybackReady(true);
      }
    } catch (error) {
      console.error(`Failed to generate segment ${segment.segmentNumber}:`, error);
      
      // Extract detailed error information
      let errorMsg = `세그먼트 ${segment.segmentNumber} 생성 실패: `;
      if (error instanceof Error) {
        errorMsg += error.message;
        
        // Check if we should show storyboard option
        if (error.message.includes('Storyboard preview is available')) {
          errorMsg += '\n\n대안: 스토리보드 미리보기를 사용하실 수 있습니다.';
        }
      } else {
        errorMsg += '알 수 없는 오류';
      }
      
      setGenerationError(errorMsg);
    } finally {
      setCurrentGenerating(null);
    }
  };

  const handlePlaySeamless = () => {
    let currentIndex = 0;

    const playNext = () => {
      if (currentIndex < videoRefs.current.length) {
        const video = videoRefs.current[currentIndex];
        if (video) {
          video.play();
          video.onended = () => {
            currentIndex++;
            playNext();
          };
        }
      }
    };

    playNext();
  };

  const totalSegments = typeof videoLength === 'number' ? 1 : parseInt(videoLength) / 8;
  
  const handleDownloadVideo = (videoUrl: string, fileName: string) => {
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  
  const handleDownloadAllVideos = () => {
    segments.forEach((segment) => {
      if (segment.videoUrl && !segment.videoUrl.startsWith('storyboard:')) {
        const fileName = adminDriveService.generateFileName(topic, segment.segmentNumber, segment.duration);
        setTimeout(() => {
          handleDownloadVideo(segment.videoUrl!, fileName);
        }, segment.segmentNumber * 500); // Stagger downloads
      }
    });
  };

  return (
    <div className="mt-8 space-y-6">
      {/* Header with Quota Display */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-slate-800/60 p-6 rounded-2xl border border-slate-700">
          <h3 className="text-xl font-bold text-white mb-2">
            {typeof videoLength === 'number' ? `${videoLength}초 커스텀 비디오` : `${videoLength}초 전체 스토리 (${totalSegments}개 세그먼트)`}
          </h3>
          <p className="text-slate-400 mb-3">
            {typeof videoLength === 'number' 
              ? '단일 연속 비디오로 생성됩니다.'
              : '각 8초 세그먼트가 매끄럽게 연결되어 하나의 완전한 스토리를 만듭니다.'}
          </p>
          
          {/* Download all videos button */}
          {segments.some(s => s.videoUrl && !s.videoUrl.startsWith('storyboard:')) && (
            <button
              onClick={handleDownloadAllVideos}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all"
              title="모든 비디오 다운로드"
            >
              <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              전체 다운로드 ({segments.filter(s => s.videoUrl && !s.videoUrl.startsWith('storyboard:')).length}개)
            </button>
          )}
        </div>
        <QuotaDisplay refreshTrigger={quotaRefreshTrigger} />
      </div>

      {/* Error Message */}
      {generationError && (
        <div className="bg-red-900/30 border border-red-700/50 text-red-400 p-4 rounded-lg mb-4">
          <p className="font-semibold">⚠️ 오류 발생</p>
          <p className="text-sm mt-1">{generationError}</p>
        </div>
      )}

      {/* Segments */}
      <div className="grid gap-4">
        {segments.map((segment, index) => (
          <div 
            key={segment.segmentNumber}
            className="bg-slate-900/50 p-4 rounded-lg border border-slate-600"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-lg font-semibold text-white">
                  세그먼트 {segment.segmentNumber} (시간: {(index * 8)}-{(index + 1) * 8}초)
                </h4>
                {segment.transition && (
                  <p className="text-sm text-indigo-400 mt-1">
                    연결: {segment.transition}
                  </p>
                )}
              </div>
              
              {!segment.videoUrl && (
                <button
                  onClick={() => handleGenerateSegment(segment)}
                  disabled={currentGenerating !== null || !quotaAvailable}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    currentGenerating !== null || !quotaAvailable
                      ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                      : remainingQuota === 1
                      ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  }`}
                  title={!quotaAvailable ? '일일 한도 초과' : `남은 생성 횟수: ${remainingQuota}`}
                >
                  {currentGenerating === segment.segmentNumber ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      생성 중...
                    </span>
                  ) : !quotaAvailable ? (
                    '한도 초과'
                  ) : (
                    `영상 생성 (${remainingQuota}회 남음)`
                  )}
                </button>
              )}
              
              {/* Download button for segment */}
              {segment.videoUrl && !segment.videoUrl.startsWith('storyboard:') && !segment.videoUrl.startsWith('data:') && (
                <button
                  onClick={() => {
                    const fileName = adminDriveService.generateFileName(topic, segment.segmentNumber, segment.duration);
                    handleDownloadVideo(segment.videoUrl!, fileName);
                  }}
                  className="ml-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all"
                  title="비디오 다운로드"
                >
                  <svg className="w-5 h-5 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  다운로드
                </button>
              )}
            </div>

            {/* Script Preview - Full Text */}
            <div className="bg-slate-800/50 p-4 rounded mb-3">
              <h5 className="text-slate-300 font-medium mb-2">스크립트 내용</h5>
              <div className="max-h-96 overflow-y-auto">
                <pre className="text-slate-300 text-sm whitespace-pre-wrap">
                  {segment.scriptContent}
                </pre>
              </div>
            </div>

            {/* Video Display */}
            {segment.videoUrl && (
              <div className="mt-3">
                {segment.videoUrl.startsWith('veo:') ? (
                  // Veo-generated video
                  <div className="w-full aspect-[9/16] bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg flex items-center justify-center">
                    <div className="text-center text-white p-6">
                      <svg className="w-20 h-20 mx-auto mb-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <p className="text-xl font-bold mb-2">🎬 Veo AI 비디오</p>
                      <p className="text-sm opacity-90 mb-2">세그먼트 {segment.segmentNumber}</p>
                      <p className="text-xs opacity-75">Google Veo로 생성된 실제 비디오</p>
                      <p className="text-xs mt-2 opacity-60">비디오 ID: {segment.videoUrl.substring(4, 20)}...</p>
                    </div>
                  </div>
                ) : segment.videoUrl.startsWith('data:video/') ? (
                  // Enhanced storyboard or placeholder display
                  <div className="w-full">
                    {(() => {
                      try {
                        // Try to decode storyboard data
                        if (segment.videoUrl.startsWith('data:video/storyboard')) {
                          const base64Data = segment.videoUrl.split(',')[1] || segment.videoUrl.split(';base64,')[1];
                          const jsonStr = decodeURIComponent(escape(atob(base64Data)));
                          const storyboardData = JSON.parse(jsonStr);
                          
                          if (storyboardData.frames && storyboardData.frames.length > 0) {
                            // Display storyboard frames
                            return (
                              <div className="space-y-3">
                                <div className="bg-slate-800/50 p-3 rounded-lg">
                                  <h5 className="text-indigo-300 font-semibold mb-2">
                                    📹 스토리보드 - 세그먼트 {segment.segmentNumber}
                                  </h5>
                                  <div className="grid grid-cols-2 gap-2">
                                    {storyboardData.frames.map((frame: any, frameIndex: number) => (
                                      <div key={frameIndex} className="bg-slate-700/50 p-2 rounded hover:bg-slate-700/70 transition-colors">
                                        <div className="text-xs text-indigo-400 font-semibold mb-1">{frame.time}</div>
                                        <div className="aspect-[9/16] bg-gradient-to-br from-slate-600 to-slate-700 rounded overflow-hidden relative group">
                                          <div className="absolute inset-0 flex flex-col justify-between p-2">
                                            <div className="text-xs text-slate-200 bg-black/50 rounded p-1">
                                              {frame.description?.substring(0, 60)}...
                                            </div>
                                            {frame.colors && (
                                              <div className="flex justify-center gap-1">
                                                {frame.colors.slice(0, 4).map((color: string, colorIndex: number) => (
                                                  <div
                                                    key={colorIndex}
                                                    className="w-6 h-6 rounded shadow-lg border border-white/20"
                                                    style={{ backgroundColor: color }}
                                                    title={color}
                                                  />
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                          {frame.imagePrompt && (
                                            <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity p-2 overflow-y-auto">
                                              <div className="text-xs text-white">
                                                <div className="font-semibold mb-1 text-indigo-300">AI 이미지 프롬프트:</div>
                                                <div className="text-slate-200">{frame.imagePrompt}</div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                        <div className="text-xs mt-1">
                                          {frame.motion && (
                                            <div className="text-slate-400">
                                              <span className="text-slate-500">모션:</span> {frame.motion.substring(0, 40)}...
                                            </div>
                                          )}
                                          {frame.sound && (
                                            <div className="text-slate-400">
                                              <span className="text-slate-500">사운드:</span> {frame.sound.substring(0, 40)}...
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {storyboardData.veoPrompt && (
                                    <div className="mt-3 p-2 bg-indigo-900/30 rounded">
                                      <div className="text-xs text-indigo-300 font-semibold mb-1">
                                        🎬 Veo API 프롬프트 (향후 사용 가능):
                                      </div>
                                      <div className="text-xs text-slate-300">
                                        {storyboardData.veoPrompt}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500 text-center">
                                  Gemini API로 생성된 상세 스토리보드 • {storyboardData.duration}
                                  <br />
                                  <span className="text-indigo-400">프레임 위에 마우스를 올리면 AI 이미지 프롬프트를 볼 수 있습니다</span>
                                </div>
                              </div>
                            );
                          }
                        }
                        
                        // Fallback to simple placeholder
                        return (
                          <div className="w-full aspect-[9/16] bg-gradient-to-br from-indigo-600 to-purple-700 rounded-lg flex items-center justify-center">
                            <div className="text-center text-white p-6">
                              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              <p className="text-lg font-semibold mb-2">비디오 세그먼트 {segment.segmentNumber}</p>
                              <p className="text-sm opacity-90">비디오가 생성되었습니다</p>
                              <p className="text-xs mt-2 opacity-75">Gemini API 스토리보드</p>
                            </div>
                          </div>
                        );
                      } catch (error) {
                        console.error("Error parsing video data:", error);
                        // Simple placeholder on error
                        return (
                          <div className="w-full aspect-[9/16] bg-gradient-to-br from-indigo-600 to-purple-700 rounded-lg flex items-center justify-center">
                            <div className="text-center text-white p-6">
                              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              <p className="text-lg font-semibold mb-2">비디오 세그먼트 {segment.segmentNumber}</p>
                              <p className="text-sm opacity-90">비디오가 생성되었습니다</p>
                            </div>
                          </div>
                        );
                      }
                    })()}
                  </div>
                ) : (
                  // Actual video player for real video URLs
                  <video 
                    ref={el => videoRefs.current[index] = el}
                    controls={!playbackReady}
                    className="w-full rounded-lg"
                    src={segment.videoUrl}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Seamless Playback Control */}
      {playbackReady && (
        <div className="bg-indigo-900/30 p-6 rounded-2xl border border-indigo-700">
          <h4 className="text-xl font-bold text-indigo-300 mb-3">
            ✨ 매끄러운 재생 준비 완료!
          </h4>
          <p className="text-slate-400 mb-4">
            모든 세그먼트가 준비되었습니다. 아래 버튼을 눌러 전체 스토리를 끊김 없이 감상하세요.
          </p>
          <button
            onClick={handlePlaySeamless}
            className="w-full py-3 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition-all transform hover:scale-105"
          >
            🎬 전체 스토리 재생 ({videoLength}초)
          </button>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-slate-800/40 p-4 rounded-lg border border-slate-600">
        <h5 className="text-indigo-300 font-semibold mb-2">💡 매끄러운 연결 시스템</h5>
        <ul className="text-slate-400 text-sm space-y-1">
          <li>• 각 세그먼트는 다음 세그먼트로 자연스럽게 이어집니다</li>
          <li>• 전체 스토리는 {videoLength}초 동안 완성됩니다</li>
          <li>• 세그먼트별 생성 또는 전체 재생이 가능합니다</li>
        </ul>
      </div>
    </div>
  );
};

export default SeamlessVideoDisplay;